import React from 'react';
import { Button } from '@/components/ui/button';
import { Users, Trash2, ArrowRight, Clock, Check, Repeat } from 'lucide-react';
import { describeRecurrence } from '@/lib/recurrenceState';
import PriorityCheckbox from '@/components/shared/PriorityCheckbox';
import { SubtaskProgressChip } from '@/components/shared/Subtasks';
import { format, parseISO } from 'date-fns';
import { useDesign } from '@/contexts/DesignContext';
import { priorityDot } from '@/components/studio/planner/parts';

/** Notification-click highlight: yellow ring + one-time scroll into view. */
export function HighlightRing({ className = 'rounded-lg' }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    ref.current?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);
  return <div ref={ref} className={`notif-highlight-ring ${className}`} />;
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * SharedTasksSection — the "shared tasks bubble" shown above regular tasks.
 * Used by the web Planner Day view, the comms Tasks view and the Dashboard widget.
 *
 * tasks: already-visible shared tasks (see visibleSharedTasks)
 * onToggle(task, completed) / onDelete(task)
 * highlightId: task id to pulse with the notification highlight ring
 * setHoveredItem: App.js hover registry, so the universal hover + Delete-key
 *   shortcut works here like it does on personal tasks (2026-09-09)
 * isOwner: church owners may delete any shared item (backend rule: creator or owner)
 */
export function SharedTasksSection({ tasks, currentUserId, onToggle, onDelete, onEdit, highlightId, compact = false, setHoveredItem, isOwner = false }) {
  const { isStudio } = useDesign();
  if (!tasks || tasks.length === 0) return null;
  const today = todayStr();

  // Studio (104 / 106): the violet bubble becomes a quiet "Shared with you"
  // section of ordinary rows inside whichever card is hosting the list.
  if (isStudio) {
    return (
      <div data-testid="shared-tasks-section">
        <div className="st-seclabel"><Users className="st-secicon st-secicon-flush" />Shared with you<span className="st-cnt">{tasks.length}</span></div>
        {tasks.map(task => {
          const overdue = !task.completed && task.due_date && task.due_date < today;
          const canEdit = !!onEdit && (task.created_by === currentUserId || task.assigned_to === currentUserId);
          const canDelete = !!onDelete && (task.created_by === currentUserId || isOwner);
          const subs = task.subtasks || [];
          const other = task.assigned_to === currentUserId ? task.created_by_name : task.assigned_to_name;
          return (
            <div
              key={task.id}
              className={`st-prow${task.completed ? ' st-done' : ''}`}
              onClick={canEdit ? () => onEdit(task) : undefined}
              onMouseEnter={canDelete && setHoveredItem ? () => setHoveredItem({ type: 'shared_task', id: task.id, data: task, deleteFn: () => onDelete(task) }) : undefined}
              onMouseLeave={setHoveredItem ? () => setHoveredItem(null) : undefined}
              data-testid={`shared-task-${task.id}`}
            >
              {highlightId === task.id && <HighlightRing />}
              <button
                type="button"
                className={`st-cb${task.completed ? ' st-on' : ''}`}
                aria-label={task.completed ? 'Mark not done' : 'Mark done'}
                onClick={(e) => { e.stopPropagation(); onToggle(task, !task.completed); }}
                data-testid={`shared-task-check-${task.id}`}
              >
                {task.completed ? <Check /> : null}
              </button>
              <span className="st-pd" style={{ background: priorityDot(task.priority_level) }} />
              <span className="st-txt">
                <span>{task.title}</span>
                {subs.length > 0 && (
                  <span className="st-chip">{subs.filter(s => s.completed).length} / {subs.length}</span>
                )}
                {task.is_recurring && (
                  <span className="st-chip" data-testid={`shared-task-repeat-${task.id}`} title={describeRecurrence(task) || 'Repeats'}>
                    <Repeat className="w-3 h-3" aria-label={describeRecurrence(task) || 'Repeats'} />
                  </span>
                )}
                {other && <span className="st-av">{(other || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()}</span>}
              </span>
              {/* D24 (120, owner re-review): a shared row sits in the same list as a
                  personal one, so its meta reads in the same order — time, then
                  Overdue on the right-hand column. Studio branch only. */}
              <span className="st-time">
                {task.completed
                  ? (task.completed_by_name ? `✓ ${task.completed_by_name}` : 'Done')
                  : (task.due_date ? format(parseISO(task.due_date), 'EEE') : '')}
              </span>
              {overdue && <span className="st-over" data-testid={`shared-task-overdue-${task.id}`}>Overdue</span>}
              {/* a shared row draws "repeats" as a chip in the title cluster, so its
                  glyph slot is always empty — it is here only to keep this row's
                  columns on the same lines as the personal rows beside it. */}
              <span className="st-repslot" />
              {canDelete && (
                <span className="st-acts">
                  <button type="button" className="st-ibtn" aria-label="Delete shared task"
                          onClick={(e) => { e.stopPropagation(); onDelete(task); }}
                          data-testid={`shared-task-delete-${task.id}`}><Trash2 /></button>
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-violet-200 dark:border-violet-500/30 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-500/10 dark:to-indigo-500/10 ${compact ? 'p-2.5 mb-2' : 'p-3 mb-4'}`}
      data-testid="shared-tasks-section"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Users className="w-3.5 h-3.5 text-violet-500 dark:text-violet-400" />
        <span className="text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">Shared Tasks</span>
        <span className="text-xs text-violet-400 dark:text-violet-400/70">{tasks.filter(t => t.completed).length}/{tasks.length}</span>
      </div>
      <div className="space-y-1">
        {tasks.map(task => {
          const overdue = !task.completed && task.due_date && task.due_date < today;
          const future = !task.completed && task.due_date && task.due_date > today;
          const fromOther = task.assigned_to === currentUserId && task.created_by !== currentUserId;
          const toOther = task.created_by === currentUserId && task.assigned_to !== currentUserId;
          const canEdit = !!onEdit && (task.created_by === currentUserId || task.assigned_to === currentUserId);
          const canDelete = !!onDelete && (task.created_by === currentUserId || isOwner);
          const subs = task.subtasks || [];
          return (
            <div
              key={task.id}
              onClick={canEdit ? () => onEdit(task) : undefined}
              onMouseEnter={canDelete && setHoveredItem ? () => setHoveredItem({ type: 'shared_task', id: task.id, data: task, deleteFn: () => onDelete(task) }) : undefined}
              onMouseLeave={setHoveredItem ? () => setHoveredItem(null) : undefined}
              className={`relative group flex items-center gap-2 py-1.5 px-2 rounded-lg transition-colors ${canEdit ? 'cursor-pointer' : ''} ${
                overdue
                  ? 'bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/40'
                  : 'bg-white/70 dark:bg-white/[0.04] border border-transparent hover:border-violet-200 dark:hover:border-violet-500/30'
              }`}
              data-testid={`shared-task-${task.id}`}
            >
              {highlightId === task.id && <HighlightRing />}
              <PriorityCheckbox
                priority={task.priority_level}
                checked={!!task.completed}
                onCheckedChange={() => onToggle(task, !task.completed)}
                onClick={(e) => e.stopPropagation()}
                className="border-violet-300 dark:border-violet-500/40 data-[state=checked]:bg-violet-500 data-[state=checked]:border-violet-500"
                data-testid={`shared-task-check-${task.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className={`text-sm flex items-center gap-1.5 flex-wrap ${task.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>
                  <span className="truncate">{task.title}</span>
                  {task.is_recurring && (
                    <span
                      className="inline-flex flex-shrink-0"
                      data-testid={`shared-task-repeat-${task.id}`}
                      title={describeRecurrence(task) || 'Repeats'}
                    >
                      <Repeat className="w-3 h-3 text-violet-400 dark:text-violet-300/70" aria-label={describeRecurrence(task) || 'Repeats'} />
                    </span>
                  )}
                  {subs.length > 0 && (
                    <SubtaskProgressChip done={subs.filter(s => s.completed).length} total={subs.length} />
                  )}
                  {overdue && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-500/25 text-amber-800 dark:text-amber-200 font-medium flex-shrink-0" data-testid={`shared-task-overdue-${task.id}`}>
                      Overdue
                    </span>
                  )}
                  {future && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-300 font-medium flex-shrink-0 inline-flex items-center gap-0.5">
                      <Clock className="w-2.5 h-2.5" />{format(parseISO(task.due_date), 'MMM d')}
                    </span>
                  )}
                </div>
                {!compact && (
                  <div className="text-[11px] text-violet-500/80 dark:text-violet-300/60 flex items-center gap-1">
                    {fromOther && <>from {task.created_by_name}</>}
                    {toOther && (<><ArrowRight className="w-2.5 h-2.5" />{task.assigned_to_name}</>)}
                    {task.completed && task.completed_by_name && (
                      <span className="text-emerald-600 dark:text-emerald-400">✓ {task.completed_by_name}</span>
                    )}
                  </div>
                )}
              </div>
              {canDelete && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0"
                  onClick={(e) => { e.stopPropagation(); onDelete(task); }}
                  data-testid={`shared-task-delete-${task.id}`}
                >
                  <Trash2 className="w-3 h-3 text-slate-400 dark:text-slate-500 hover:text-red-500" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SharedTasksSection;
